import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { createOrder, fetchConfig } from "@/lib/api";
import { MenuStep } from "@/components/MenuStep";

const DEFAULT_LOCATIONS = ["Aylesbury", "Whitehouse", "Bletchley", "Glebefarm", "Hatfield", "Uxbridge"];

const initialDetails = {
  customer_name: "",
  email: "",
  phone: "",
  pickup_delivery: "",
  spoke_location: "",
  lunch: false,
  dinner: false,
  requested_date: "",
  requested_time: "",
  special_instructions: "",
};

const Field = ({ label, required, children, testId, hint }) => (
  <div className="flex flex-col gap-1.5" data-testid={testId}>
    <label className="label-uppercase flex items-center gap-1">
      {required && <span className="text-dotr-primary">*</span>}
      {label}
    </label>
    {children}
    {hint && <span className="font-mono text-[11px] text-dotr-inkmuted">{hint}</span>}
  </div>
);

const MealCard = ({ label, time, checked, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testId}
    aria-pressed={checked}
    className={`flex-1 text-left p-4 border-2 border-dotr-ink transition-all ${
      checked
        ? "bg-dotr-ink text-white shadow-brutalSm translate-x-[2px] translate-y-[2px]"
        : "bg-white text-dotr-ink shadow-brutal hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-brutalSm"
    }`}
  >
    <div className="flex items-center justify-between">
      <div>
        <div className="font-heading font-semibold uppercase tracking-wider text-lg">{label}</div>
        <div className={`font-mono text-xs ${checked ? "text-white/70" : "text-dotr-inkmuted"}`}>
          Run @ {time}
        </div>
      </div>
      <div
        className={`w-6 h-6 border-2 ${
          checked ? "border-white bg-white" : "border-dotr-ink bg-white"
        } flex items-center justify-center`}
      >
        {checked && <CheckCircle2 size={14} className="text-dotr-ink" />}
      </div>
    </div>
  </button>
);

const StepIndicator = ({ step }) => (
  <div className="flex items-center gap-2" data-testid="step-indicator">
    {[1, 2].map((n) => (
      <React.Fragment key={n}>
        <div
          className={`w-8 h-8 flex items-center justify-center border-2 border-dotr-ink font-heading font-bold ${
            step >= n ? "bg-dotr-primary text-white" : "bg-white text-dotr-ink"
          }`}
          data-testid={`step-dot-${n}`}
        >
          {n}
        </div>
        {n === 1 && <div className="w-8 h-[2px] bg-dotr-ink" />}
      </React.Fragment>
    ))}
    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted ml-2">
      Step {step} / 2 · {step === 1 ? "Details" : "Menu"}
    </span>
  </div>
);

export const NewOrderForm = ({ onCreated }) => {
  const [step, setStep] = useState(1);
  const [details, setDetails] = useState(initialDetails);
  const [items, setItems] = useState({}); // { itemName: qty }
  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState(DEFAULT_LOCATIONS);

  useEffect(() => {
    fetchConfig()
      .then((c) => Array.isArray(c?.spoke_locations) && c.spoke_locations.length && setLocations(c.spoke_locations))
      .catch(() => {});
  }, []);

  const totalQty = useMemo(
    () => Object.values(items).reduce((s, n) => s + (parseInt(n, 10) || 0), 0),
    [items]
  );
  const [menuPrices, setMenuPrices] = useState({});
  const grandTotal = useMemo(
    () =>
      Object.entries(items).reduce(
        (sum, [name, qty]) => sum + (menuPrices[name] || 0) * (parseInt(qty, 10) || 0),
        0
      ),
    [items, menuPrices]
  );

  const update = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setDetails((f) => ({ ...f, [key]: val }));
  };

  const goNext = () => {
    if (!details.customer_name.trim()) return toast.error("Customer name is required");
    if (!details.pickup_delivery) return toast.error("Please select Pickup or Delivery");
    if (!details.spoke_location) return toast.error("Please select a Spoke Location");
    if (!details.lunch && !details.dinner) return toast.error("Pick Lunch or Dinner (or both)");
    if (!details.requested_date || !details.requested_time)
      return toast.error("Requested date & time are required");
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrevious = () => {
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetAll = () => {
    setDetails(initialDetails);
    setItems({});
    setStep(1);
  };

  const submit = async () => {
    if (Object.keys(items).length === 0) {
      return toast.error("Select at least one menu item.");
    }
    if (totalQty < 1) {
      return toast.error("Total quantity must be at least 1.");
    }
    // Map items to backend payload with categories
    const itemList = Object.entries(items).map(([name, quantity]) => ({
      // category is set on the backend side based on menu name; client side use placeholder
      category: "",
      name,
      quantity: parseInt(quantity, 10) || 1,
    }));

    setSubmitting(true);
    try {
      const requested_at = `${details.requested_date}T${details.requested_time}:00`;
      const payload = {
        customer_name: details.customer_name.trim(),
        email: details.email.trim() || null,
        phone: details.phone.trim() || null,
        pickup_delivery: details.pickup_delivery,
        spoke_location: details.spoke_location,
        lunch: details.lunch,
        dinner: details.dinner,
        requested_at,
        special_instructions: details.special_instructions.trim() || null,
        items: itemList,
      };
      await createOrder(payload);
      toast.success("Order captured.", {
        description: `${details.customer_name} → ${details.spoke_location} · ${totalQty} item${totalQty === 1 ? "" : "s"}`,
      });
      resetAll();
      onCreated?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save order.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="brutal-card p-6 md:p-8 lg:p-10 animate-fade-up" data-testid="new-order-form">
      <div className="flex items-end justify-between mb-7 flex-wrap gap-3 border-b-2 border-dotr-ink pb-5">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted mb-1">
            Form / Daily
          </div>
          <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight uppercase leading-[0.95]">
            DOTR Spoke<br />Order Flow
          </h1>
        </div>
        <StepIndicator step={step} />
      </div>

      {step === 1 && (
        <div data-testid="step-1-details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            <Field label="Customer Name" required testId="field-customer-name">
              <input
                data-testid="input-customer-name"
                className="brutal-input h-12 px-3 font-body text-base w-full"
                value={details.customer_name}
                onChange={update("customer_name")}
                placeholder="Jane Doe"
                maxLength={120}
              />
            </Field>
            <Field label="Phone" testId="field-phone">
              <input
                data-testid="input-phone"
                className="brutal-input h-12 px-3 font-body text-base w-full"
                value={details.phone}
                onChange={update("phone")}
                placeholder="+44 ..."
                inputMode="tel"
              />
            </Field>

            <Field label="Email" testId="field-email" hint="Optional, for confirmations.">
              <input
                data-testid="input-email"
                type="email"
                className="brutal-input h-12 px-3 font-body text-base w-full"
                value={details.email}
                onChange={update("email")}
                placeholder="jane@example.com"
              />
            </Field>

            <Field label="Pickup / Delivery" required testId="field-pickup-delivery">
              <select
                data-testid="select-pickup-delivery"
                className="brutal-input h-12 px-3 font-body text-base w-full bg-white"
                value={details.pickup_delivery}
                onChange={update("pickup_delivery")}
              >
                <option value="">— Select —</option>
                <option value="Pickup">Pickup</option>
                <option value="Delivery">Delivery</option>
              </select>
            </Field>

            <Field label="Spoke Location" required testId="field-spoke-location">
              <select
                data-testid="select-spoke-location"
                className="brutal-input h-12 px-3 font-body text-base w-full bg-white"
                value={details.spoke_location}
                onChange={update("spoke_location")}
              >
                <option value="">— Select —</option>
                {locations.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </Field>

            <div className="md:col-span-2">
              <Field label="Meal Service" required testId="field-meal">
                <div className="flex flex-col sm:flex-row gap-4 mt-1">
                  <MealCard
                    label="Lunch"
                    time="12:45 PM"
                    checked={details.lunch}
                    onChange={(v) => setDetails((f) => ({ ...f, lunch: v }))}
                    testId="meal-lunch-card"
                  />
                  <MealCard
                    label="Dinner"
                    time="4:30 PM"
                    checked={details.dinner}
                    onChange={(v) => setDetails((f) => ({ ...f, dinner: v }))}
                    testId="meal-dinner-card"
                  />
                </div>
              </Field>
            </div>

            <Field label="Requested Date" required testId="field-requested-date">
              <input
                data-testid="input-requested-date"
                type="date"
                className="brutal-input h-12 px-3 font-body text-base w-full"
                value={details.requested_date}
                onChange={update("requested_date")}
              />
            </Field>

            <Field label="Requested Time" required testId="field-requested-time">
              <input
                data-testid="input-requested-time"
                type="time"
                className="brutal-input h-12 px-3 font-body text-base w-full"
                value={details.requested_time}
                onChange={update("requested_time")}
              />
            </Field>

            <div className="md:col-span-2">
              <Field label="Special Instructions" testId="field-special-instructions">
                <textarea
                  data-testid="input-special-instructions"
                  className="brutal-input p-3 font-body text-base w-full min-h-[110px]"
                  rows={4}
                  value={details.special_instructions}
                  onChange={update("special_instructions")}
                  placeholder="Allergies, packaging notes, delivery instructions…"
                />
              </Field>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-end gap-3 pt-6 border-t-2 border-dotr-ink">
            <button
              type="button"
              onClick={resetAll}
              data-testid="btn-reset-form"
              className="brutal-btn-ghost px-5 py-3 text-sm"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={goNext}
              data-testid="btn-next-step"
              className="brutal-btn px-7 py-3 text-sm flex items-center gap-2"
            >
              Next: Menu <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="step-2-menu">
          <MenuStep value={items} onChange={setItems} totalQty={totalQty} onPricesLoaded={setMenuPrices} />

          <div className="mt-8 flex items-center justify-between gap-3 pt-6 border-t-2 border-dotr-ink flex-wrap">
            <button
              type="button"
              onClick={goPrevious}
              data-testid="btn-previous-step"
              className="brutal-btn-ghost px-5 py-3 text-sm flex items-center gap-2"
            >
              <ChevronLeft size={16} /> Previous
            </button>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex flex-col items-end">
                <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">
                  {Object.keys(items).length} item types · {totalQty} qty
                </span>
                <span className="font-heading font-semibold text-2xl text-dotr-primary tracking-tighter" data-testid="footer-grand-total">
                  £{grandTotal.toFixed(2)}
                </span>
              </div>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="btn-submit-order"
                className="brutal-btn px-7 py-3 text-sm flex items-center gap-2 disabled:opacity-70"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {submitting ? "Capturing…" : "Capture Order →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
